/**
 * Запуск функции после следующего кадра отрисовки.
 * Полезно для отложенного выполнения задач, чтобы не блокировать отрисовку UI.
 */
export function runAfterPaint(fn) {
    requestAnimationFrame(() => {
        setTimeout(fn, 0);
    });
}

/**
 * Запуск функции в период простоя, но не позднее заданного таймаута.
 */
export function runWhenIdle(fn, { timeout = 2000 } = {}) {
    if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(() => fn(), { timeout });
        return;
    }
    setTimeout(fn, 0);
}

/**
 * Явный МАКРОтаск, выполнение которого в основном потоке
 * будет отложено до завершения текущих микротасков и рендеринга.
 */
export function yieldToMain() {
    return new Promise(resolve => {
        window.setTimeout(resolve, 0);
    });
}

/**
 * Запуск последовательности шагов с разрывами (каждый шаг стартует в отдельной задаче).
 */
export async function runSequence(steps, { gap = 0 } = {}) {
    for (const step of steps) {
        // Пустые шаги пропускаются, чтобы не создавать лишних задержек
        if (step) step();
        else continue;
        // eslint-disable-next-line no-await-in-loop
        await yieldToMain();
        if (gap > 0) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise(r => {
                window.setTimeout(r, gap);
            });
        }
    }
}

/**
 * Запуск действия после первого взаимодействия пользователя с страницей.
 */
export async function onFirstInteraction({ timeout = 8000 } = {}) {
    await new Promise<void>(resolve => {
        const options = {
            capture: true,
            passive: true,
            once: true
        };
        let done = false;

        const finish = () => {
            if (done) return;
            done = true;
            // eslint-disable-next-line no-use-before-define
            cleanup();
            resolve();
        };

        const cleanup = () => {
            window.removeEventListener('pointerdown', finish, options);
            window.removeEventListener('keydown', finish, options);
            window.removeEventListener('touchstart', finish, options);
            window.removeEventListener('wheel', finish, options);
            window.removeEventListener('scroll', finish, options);
        };

        window.addEventListener('pointerdown', finish, options);
        window.addEventListener('keydown', finish, options);
        window.addEventListener('touchstart', finish, options);
        window.addEventListener('wheel', finish, options);
        window.addEventListener('scroll', finish, options);
        window.setTimeout(finish, timeout);
    });
}
